import { useEffect, useMemo, useReducer, useState } from 'react';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { ClaimEigenschappen, MaakClaimOpdracht, WijzigenInzetOpdracht } from '@/queries/mutaties/opdrachten';
import { MutatieResponse, useMutaties } from '@/queries/mutaties/useMutaties';
import useClientState from '@/state/useClientState';
import { startEindDatumTijdVerschillen } from '@/utils/dateUtils';
import { getDateVariations } from '@/utils/dateVariations';
import getClaimsoorten from '@/utils/staticData/claimsoorten';

import claimDialogReducer, { detailsToClaimState, parseInput } from './claimDialogReducer';
import { type ClaimFormType, LabelType } from './types';

const valueLabelHelper = (value: string, label: string, subLabel?: string): LabelType => ({
  value,
  label: (
    <span>
      {label}
      {subLabel && <small>&nbsp;-&nbsp;{subLabel}</small>}
    </span>
  ),
});

const useClaimDialog = (details: WerklijnContextMenuDetails, handleOpenChange: (open: boolean) => void) => {
  const lockedWerklijnVersie = details.werklijnVersie;
  const werklijnen = useClientState((state) => state.werklijnen);
  const werklijnVersieFromState = details.werklijnId && werklijnen[details.werklijnId]?.versie;
  const [warningMessage, setWarningMessage] = useState<boolean>(false);
  const [updatedDetails, setUpdatedDetails] = useState<WerklijnContextMenuDetails>(details);

  useEffect(() => {
    const shouldUpdate = werklijnVersieFromState && lockedWerklijnVersie !== werklijnVersieFromState;
    setWarningMessage(!!shouldUpdate);
    if (!!shouldUpdate && details) {
      setUpdatedDetails({ ...details, werklijnVersie: werklijnVersieFromState as number });
    }
  }, [lockedWerklijnVersie, werklijnVersieFromState]);

  const [claimState, dispatch] = useReducer(claimDialogReducer, updatedDetails, detailsToClaimState);
  const { makenClaim, wijzigenClaim, verwijderenClaim } = useMutaties();

  const dateVariations = getDateVariations();

  const [claimsoorten, setClaimsoorten] = useState<LabelType[]>(
    getClaimsoorten().map((soort) => valueLabelHelper(soort.value, soort.label, soort.subLabel)),
  );

  /** Create a new claimsoort on the fly (for this use only). */
  const handleClaimsoortCreate = (onbekendeClaimsoort: string) => {
    const pseudoOption = valueLabelHelper(
      onbekendeClaimsoort,
      onbekendeClaimsoort,
      'Claimcode onbekend in de Bam client',
    );
    // Add the unkown claimsoort as an option
    setClaimsoorten((prev) => {
      return [...prev, pseudoOption];
    });
    return pseudoOption;
  };

  /** Get the default claimsoort option, this also handles unknown claimsoorten and temporarily adds them when user enters one. */
  const claimsoortOptionValue = useMemo(() => {
    const bekendeClaimsoorten = claimsoorten.filter((opt) => opt.value === claimState.eigenschappen.claimsoort);
    if (!bekendeClaimsoorten.length) {
      const pseudoOption = handleClaimsoortCreate(claimState.eigenschappen.claimsoort);
      return [pseudoOption];
    }
    return bekendeClaimsoorten;
  }, [claimsoorten, claimState.eigenschappen.claimsoort]);

  useEffect(() => {
    dispatch({ type: 'updateDetails', value: updatedDetails });
  }, [updatedDetails]);

  // reset all values
  const resetDialog = (closeButtonCLick: boolean) => {
    if (closeButtonCLick) {
      handleOpenChange(false);
    }
    dispatch({ type: 'reset' });
  };

  const _validateClaimEigenschappen = (eigenschappen: ClaimEigenschappen): boolean => {
    if (
      !eigenschappen.vertrek ||
      !eigenschappen.aankomst ||
      eigenschappen.vertrek === '' ||
      eigenschappen.aankomst === ''
    ) {
      // Wanneer aankomst niet is gevuld, wordt het gedefault naar de vertrekwaarde.
      dispatch({ type: 'invalidate', field: 'vertrek', value: 'Vertrek is verplicht' });
      return false;
    }
    return true;
  };

  const _validateVerwijderClaimDetails = (updatedDetails: WerklijnContextMenuDetails): boolean => {
    return !(!details.wleId && !updatedDetails.werklijnId && !updatedDetails.werklijnVersie);
  };

  const verwijderClaim = (updatedDetails: WerklijnContextMenuDetails) => {
    if (!_validateVerwijderClaimDetails(updatedDetails)) return;
    (async () => {
      const result = await verwijderenClaim({
        teVerwijderenClaimId: details.wleId,
        betrokkenWerklijn: {
          id: updatedDetails?.werklijnId,
          versie: updatedDetails?.werklijnVersie,
        },
      });
      if (result.status === 'SUCCES') resetDialog(true);
    })();
  };

  const _validateFormTimes = (target: EventTarget & ClaimFormType): boolean => {
    const validate = startEindDatumTijdVerschillen(
      parseInput(target.beginDatum.value, target.beginTijd.value),
      parseInput(target.eindDatum.value, target.eindTijd.value),
    );

    if (validate.isAfter || validate.isLessThan1Minute) {
      dispatch({ type: 'updateDateTime', field: 'beginDatum', value: target.beginDatum.value });
      dispatch({ type: 'updateDateTime', field: 'beginTijd', value: target.beginTijd.value });
      dispatch({ type: 'updateDateTime', field: 'eindDatum', value: target.eindDatum.value });
      dispatch({ type: 'updateDateTime', field: 'eindTijd', value: target.eindTijd.value });
      return false;
    }
    return true;
  };

  const _getClaimEigenschappen = (target: EventTarget & ClaimFormType): ClaimEigenschappen => ({
    ...claimState.eigenschappen,
    aankomst: claimState.eigenschappen.aankomst || claimState.eigenschappen.vertrek,
    beginDatumTijd: parseInput(target.beginDatum.value, target.beginTijd.value).toISOString(),
    eindDatumTijd: parseInput(target.eindDatum.value, target.eindTijd.value).toISOString(),
    claimsoort: target.claimsoort.value,
    toelichting: target.toelichting.value ? target.toelichting.value : null,
    bijzonderheidscode: target.bijzonderheidscode.value ? target.bijzonderheidscode.value : null,
  });

  const createClaimFormEvent = (target: EventTarget & ClaimFormType): MaakClaimOpdracht => {
    return {
      naInzetId: claimState.naInzetId,
      betrokkenWerklijn: claimState.betrokkenWerklijn,
      nieuweClaimEigenschappen: _getClaimEigenschappen(target),
    };
  };

  const updateClaimFormEvent = (target: EventTarget & ClaimFormType): WijzigenInzetOpdracht => {
    return {
      inzetId: claimState.inzetId as string,
      betrokkenWerklijn: claimState.betrokkenWerklijn,
      gewijzigdeClaimEigenschappen: _getClaimEigenschappen(target),
    };
  };

  const claimFormEvent = (target: EventTarget & ClaimFormType) => {
    if (!_validateFormTimes(target)) return;

    const eigenschappen = _getClaimEigenschappen(target);
    if (!_validateClaimEigenschappen(eigenschappen)) return;

    (async () => {
      let result: MutatieResponse;
      if (claimState.isUpdate) {
        const claim = updateClaimFormEvent(target);
        result = await wijzigenClaim(claim);
      } else {
        const claim = createClaimFormEvent(target);
        result = await makenClaim(claim);
      }
      if (result.status === 'SUCCES') resetDialog(true);
    })();
  };

  return {
    claimFormEvent,
    claimState,
    dispatch,
    claimsoorten,
    claimsoortOptionValue,
    dateVariations,
    resetDialog,
    verwijderClaim,
    handleClaimsoortCreate,
    warningMessage,
  };
};
export default useClaimDialog;
